import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Panel } from '@/components/shared/Panel';
import { appRoutes, dashboardRoutes, eventRoutes, templateRoutes } from '@/data/routes';
import { useAuth } from '@/hooks/useAuth';
import { CurrentMassesPage } from '@/pages/CurrentMassesPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { EventBuilderPage } from '@/pages/EventBuilderPage';
import { MassDetailsPage } from '@/pages/MassDetailsPage';
import { MySignupsPage } from '@/pages/MySignupsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { PlayerStatsPage } from '@/pages/PlayerStatsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { SignInPage } from '@/pages/SignInPage';
import { TemplateBuilderPage } from '@/pages/TemplateBuilderPage';
import { TemplatesPage } from '@/pages/TemplatesPage';

function RouteLoadingState(): JSX.Element {
  return (
    <div className="auth-page">
      <div className="auth-page__backdrop" />
      <Panel title="Loading session" description="Checking your Discord session and guild approval status." />
    </div>
  );
}

function getSignedInDestination(isApproved: boolean, needsOnboarding: boolean): string {
  if (needsOnboarding) {
    return '/onboarding';
  }

  if (!isApproved) {
    return '/pending-approval';
  }

  return '/masses';
}

function RootRedirect(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding } = useAuth();

  if (isLoading) {
    return <RouteLoadingState />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return <Navigate to={getSignedInDestination(isApproved, needsOnboarding)} replace />;
}

function SignInRoute(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding } = useAuth();

  if (isLoading) {
    return <RouteLoadingState />;
  }

  if (isSignedIn) {
    return <Navigate to={getSignedInDestination(isApproved, needsOnboarding)} replace />;
  }

  return <SignInPage />;
}

function ApprovedRoute(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding } = useAuth();

  if (isLoading) {
    return <RouteLoadingState />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  return <Outlet />;
}

function StaffRoute(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding, hasAnyRole } = useAuth();

  if (isLoading) {
    return <RouteLoadingState />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (!hasAnyRole(['owner', 'admin', 'officer', 'mass_manager'])) {
    return <Navigate to="/masses" replace />;
  }

  return <Outlet />;
}

function AdminOfficerRoute(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding, hasAnyRole } = useAuth();

  if (isLoading) {
    return <RouteLoadingState />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (!hasAnyRole(['owner', 'admin', 'officer'])) {
    return <Navigate to="/masses" replace />;
  }

  return <Outlet />;
}

function PendingApprovalRoute(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding } = useAuth();

  if (isLoading) {
    return <RouteLoadingState />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (needsOnboarding || isApproved) {
    return <Navigate to={getSignedInDestination(isApproved, needsOnboarding)} replace />;
  }

  return <PendingApprovalPage />;
}

function OnboardingRoute(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding } = useAuth();

  if (isLoading) {
    return <RouteLoadingState />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!needsOnboarding) {
    return <Navigate to={getSignedInDestination(isApproved, false)} replace />;
  }

  return <OnboardingPage />;
}

export function AppRouter(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/sign-in" element={<SignInRoute />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/pending-approval" element={<PendingApprovalRoute />} />
      <Route element={<ApprovedRoute />}>
        <Route path={appRoutes.masses} element={<CurrentMassesPage />} />
        <Route path="/masses/:eventId" element={<MassDetailsPage />} />
        <Route path={appRoutes.mySignups} element={<MySignupsPage />} />
        <Route path={appRoutes.profile} element={<ProfilePage />} />
        <Route path={appRoutes.playerStats} element={<PlayerStatsPage />} />
      </Route>
      <Route element={<StaffRoute />}>
        <Route path={dashboardRoutes.home} element={<DashboardPage />} />
        <Route path={eventRoutes.create} element={<EventBuilderPage />} />
        <Route path={eventRoutes.edit(':eventId')} element={<EventBuilderPage />} />
      </Route>
      <Route element={<AdminOfficerRoute />}>
        <Route path={templateRoutes.list} element={<TemplatesPage />} />
        <Route path={templateRoutes.create} element={<TemplateBuilderPage />} />
        <Route path={templateRoutes.editPattern} element={<TemplateBuilderPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
